import {
  Box,
  Button,
  IconButton,
  List,
  ListItemButton,
  ListItemDecorator,
  Tooltip,
  Typography,
} from "@mui/joy";
import { ClickAwayListener } from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getContrastYIQ } from "../helpers";
import { useAnswerMutation } from "../redux/missionMap";
import {
  setSelectedInteractor,
  setSelectedInteractorJobStageID,
} from "../redux/rhapsodyReducer";
import { useMissionMap } from "../templates/projectInvite";
import {
  pollDisplayTitle,
  pollIsAnswered,
} from "../entities/missionMap/missionMap";
import { Greetings } from "./greetings";
import { PollCard } from "./pollCard";
import { Response } from "./response";
import { useSearchParams } from "react-router-dom";

export const blueBubble = (
  <Box
    sx={{
      width: 10,
      height: 10,
      borderRadius: 10,
      background: "#027AFF",
    }}
  ></Box>
);

/**
 *
 * @returns {ReactElement} Messages page
 */
export function Messages({
  token,
  hideMessages,
}: {
  token: string;
  /** Modern calls render messages inline in the page (organisms/callMessages),
   * so the bar keeps only the polls. Set there, never for legacy. */
  hideMessages?: boolean;
}) {
  const [answer] = useAnswerMutation();
  const mission = useMissionMap(token);
  const [searchParams, setSearchParams] = useSearchParams();
  const mainToken = searchParams.get("token");
  const [open, setOpen] = useState(false);
  const [selectedJobStageID, setSelectedJobStageID] = useState<number>();
  let messages = mission?.Messages ?? [];
  const season = token[0] === "G";
  if (season) messages = mission?.seasonMessages() ?? [];
  const polls = mission?.Polls ?? [];
  const [selectedPollToken, setSelectedPollToken] = useState<string>();
  const selectedPoll = polls.find((p) => p.publicToken === selectedPollToken);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!selectedJobStageID && open) {
      dispatch(setSelectedInteractor());
      dispatch(setSelectedInteractorJobStageID());
    }
  }, [open]);

  // The chips sit on the WHITE page, so their ink is the company colour itself
  // and their tint is a wash of it.
  //
  // `color` used to be getContrastYIQ(CompanyColor) — the colour that contrasts
  // WITH the company colour, which is what you want for text drawn ON that
  // colour. Used here it made both the tint (`${color}22`) and the label the same
  // value, and for any dark org that value is #ffffff: a white label on a
  // 13%-white wash of a white page. The chips have been invisible pills showing
  // only their icon.
  const chipInk = mission?.CompanyColor ?? "#0d1b2f";
  const color = chipInk;

  const selectedMessage = messages?.find(
    (m) => m.JobStageID === selectedJobStageID,
  );

  const answerAll = () => {
    const body: {
      [token: string]: { mercuryInteractorID?: number; jobStageID?: number };
    } = {};
    const similar = mission
      ?.allMessages()
      .filter((e) => e.CreatedAt === selectedMessage?.CreatedAt);

    similar?.forEach((s) => {
      body[s.token!] = {
        mercuryInteractorID: s?.JobStageID,
        jobStageID: s?.JobStageID,
      };
    });

    answer(body);
  };

  const newMessages = messages.filter((m) => !m.Acknowledged);

  useEffect(() => {
    if (
      // On a modern call the unread message is already open in the page, so
      // popping the overlay on load would cover the thing it is announcing.
      !hideMessages &&
      newMessages?.length > 0 &&
      token == mainToken &&
      newMessages[0]?.Name !== "Contract"
    ) {
      setSelectedJobStageID(newMessages[0].JobStageID);
    }
  }, []);

  if ((hideMessages || messages?.length === 0) && polls.length === 0)
    return <Box />;
  return (
    <>
      <Box
        sx={{
          p: 2,
          display: "flex",
          gap: 1,
          overflow: "auto",
          overscrollBehavior: "contain",
          width: "100%",
          justifyContent: "center",
          backdropFilter: "blur(20px)",
          zIndex: 1302,
        }}
      >
        {(hideMessages ? [] : newMessages)
          ?.filter((m) => m.Name !== "Contract")
          .map((m) => (
            <Button
              onClick={() => setSelectedJobStageID(m.JobStageID)}
              endDecorator={!m.Acknowledged ? blueBubble : undefined}
              startDecorator={
                <img
                  style={{
                    width: 18,
                    filter:
                      m.JobStageID === selectedJobStageID
                        ? "brightness(0) invert(1)"
                        : undefined,
                  }}
                  src={m.Icon}
                />
              }
              sx={{
                zIndex: 1302,
                background:
                  m.JobStageID === selectedJobStageID ? m.Color : `${color}22`,
                "&:hover": {
                  background:
                    m.JobStageID === selectedJobStageID
                      ? m.Color
                      : `${color}33`,
                },
                flexShrink: 0,
              }}
              size="sm"
              key={m.JobStageID}
            >
              <Box
                sx={{
                  textAlign: "left",
                  // This chip's own selection, not "something is selected".
                  // The bare `|| selectedJobStageID` whitened EVERY label as
                  // soon as any message was open — and one is opened
                  // automatically — so unselected chips rendered white text on
                  // their pale `${color}22` background and the label vanished.
                  // The background and the icon were already per-chip; only
                  // this was not.
                  color:
                    m.JobStageID === selectedJobStageID
                      ? getContrastYIQ(m.Color ?? chipInk)
                      : chipInk,
                }}
              >
                {m.Name}
                <Typography
                  level="body-xs"
                  sx={{ fontSize: 8, color: "inherit" }}
                >
                  {moment(m.CreatedAt).format("lll")}
                </Typography>
              </Box>
            </Button>
          ))}
        {polls.map((p) => (
          <Button
            key={p.publicToken}
            onClick={() => {
              setSelectedPollToken(p.publicToken);
              setSelectedJobStageID(undefined);
            }}
            endDecorator={!pollIsAnswered(p) ? blueBubble : undefined}
            startDecorator={
              <i
                className="fa-solid fa-square-poll-vertical"
                style={{
                  fontSize: 14,
                  color:
                    p.publicToken === selectedPollToken
                      ? getContrastYIQ(mission?.CompanyColor ?? chipInk)
                      : chipInk,
                }}
              />
            }
            sx={{
              zIndex: 1302,
              background:
                p.publicToken === selectedPollToken
                  ? mission?.CompanyColor ?? "#027AFF"
                  : `${color}22`,
              "&:hover": {
                background:
                  p.publicToken === selectedPollToken
                    ? mission?.CompanyColor ?? "#027AFF"
                    : `${color}33`,
              },
              flexShrink: 0,
            }}
            size="sm"
          >
            <Box
              sx={{
                textAlign: "left",
                maxWidth: 160,
                // Per-chip, matching this button's own background and icon.
                color:
                  p.publicToken === selectedPollToken
                    ? getContrastYIQ(mission?.CompanyColor ?? chipInk)
                    : chipInk,
              }}
            >
              Poll
              <Typography
                level="body-xs"
                noWrap
                sx={{ fontSize: 8, color: "inherit" }}
              >
                {pollDisplayTitle(p)}
              </Typography>
            </Box>
          </Button>
        ))}
        {/* The inbox — every message, read or not. Modern calls list them in
            the page instead, so the bar is left with polls alone. */}
        {hideMessages ? null : (
        <Tooltip
          arrow
          variant="outlined"
          open={open}
          sx={{ zIndex: 999999 }}
          title={
            <ClickAwayListener onClickAway={() => setOpen(false)}>
              <List sx={{ maxWidth: 280, width: "100vw", zIndex: 999999 }}>
                {messages?.map((m) => (
                  <ListItemButton
                    selected={selectedJobStageID == m.JobStageID}
                    key={m.JobStageID}
                    onClick={() => {
                      setSelectedJobStageID(m.JobStageID);
                      setOpen(false);
                    }}
                  >
                    <ListItemDecorator>
                      <img
                        style={{
                          width: 18,
                        }}
                        src={m.Icon}
                      />
                    </ListItemDecorator>
                    <Box sx={{ textAlign: "left" }}>
                      <Typography level="body-md">{m.Name}</Typography>
                      <Typography level="body-xs">
                        {m.CreatedBy} on {moment(m.CreatedAt).format("lll")}
                      </Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />
                    <ListItemDecorator>
                      {!m.Acknowledged ? blueBubble : []}
                    </ListItemDecorator>
                  </ListItemButton>
                ))}
              </List>
            </ClickAwayListener>
          }
        >
          <IconButton
            onClick={() => setOpen(true)}
            sx={{
              zIndex: 1302,
              background: `${color}22`,
              "&:hover": {
                background: `${color}33`,
              },
              color: selectedJobStageID ? "white" : color,
            }}
            size="sm"
          >
            <i className="fa-solid fa-inbox"></i>
          </IconButton>
        </Tooltip>
        )}
      </Box>
      {selectedJobStageID || selectedPollToken ? (
        <Box
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setSelectedJobStageID(undefined);
            setSelectedPollToken(undefined);
          }}
          style={{
            position: "fixed",
            background: "rgba(0,0,0,0.6)",
            height: "100dvh",
            width: "100%",
            overflow: "auto",
            top: 0,
            left: 0,
            zIndex: 1301,
            // overflow: "scroll",
            display: "flex",
            alignItems: "start",
            justifyContent: "center",
            backdropFilter: "blur(10px)",
          }}
        >
          <Box
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            sx={{
              mt: "78px",
              gap: 2,
              display: "flex",
              width: "100%",
              maxWidth: 480,
              minHeight: "calc(100% - 78px)",
              flex: 1,
              p: 2,
              flexDirection: "column",
              position: "relative",
            }}
          >
            <IconButton
              variant="plain"
              size="lg"
              sx={{
                position: "absolute",
                right: 2,
                top: 28,
                zIndex: 9999,
                background: "white",
                borderRadius: "30px",
                boxShadow: "2px 2px 5px rgba(0,0,0,.2)",
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setSelectedJobStageID(undefined);
                setSelectedPollToken(undefined);
              }}
            >
              <i className="fa-solid fa-xmark"></i>
            </IconButton>
            {selectedPoll ? (
              <PollCard poll={selectedPoll} />
            ) : (
              <>
            <Box sx={{ textAlign: "center" }}>
              <Typography level="body-sm" sx={{ color: "white" }}>
                {selectedMessage?.CreatedBy} on{" "}
                {moment(selectedMessage?.CreatedAt).format("lll")}:
              </Typography>
            </Box>
            <Greetings
              token={token}
              overwriteMission={selectedMessage?.Data}
              hideResponse
            />
            {selectedMessage?.Data.Interactors?.length ? (
              <Response
                seasonMission={mission}
                selectedMessage={selectedMessage}
                clearSelectedMessage={() => setSelectedJobStageID(undefined)}
                token={token}
                jobStageID={selectedMessage?.JobStageID}
                overwriteMission={selectedMessage?.Data}
              />
            ) : (
              []
            )}
            {!selectedMessage?.Acknowledged &&
            (selectedMessage?.Data?.Interactors ?? [])?.length == 0 ? (
              <Button
                startDecorator={<i className="fa-solid fa-eye"></i>}
                variant="soft"
                size="lg"
                onClick={() => {
                  if (season) {
                    answerAll();
                  } else {
                    answer({
                      [mission?.PublicToken ?? ""]: {
                        mercuryInteractorID: selectedMessage?.JobStageID,
                        jobStageID: selectedMessage?.JobStageID,
                      },
                    });
                  }
                }}
              >
                Mark as read
              </Button>
            ) : (
              []
            )}
            {selectedMessage?.Inspectors?.map((i) => (
              <Box key={i.ID} sx={{ textAlign: "center" }}>
                {i.Kind === "file" ? (
                  <Button
                    onClick={() => window.open(i.Message)}
                    sx={{ mr: 1 }}
                    color="primary"
                    variant="solid"
                  >
                    {i.Title}
                  </Button>
                ) : (
                  <Typography
                    startDecorator={
                      <i className="fa-solid fa-check-double"></i>
                    }
                    level="body-sm"
                    sx={{ color: "white" }}
                  >
                    {i.Message} by {i.CreatedBy} on{" "}
                    {moment(i.CreatedAt).format("lll")}
                  </Typography>
                )}
              </Box>
            ))}
              </>
            )}
          </Box>
        </Box>
      ) : (
        []
      )}
    </>
  );
}
